import { styled } from "goober";
import { useEffect } from "react";
import { Button } from "../../Button";
import { Icon } from "../../Icon";
import { Text } from "../../Text";

type Props = {
  fileName: string;
  fullPath: string;
  size: number;
  modifiedAt: number;
  columnIndex: number;
  onRegisterCount: (columnIndex: number, count: number) => void;
  onPreview: () => void;
  onDownload: () => void;
};

const Container = styled("div")`
  flex: 1 0 280px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 12px;
  border-right: 0.5px solid var(--border-alpha);
`;

const Footer = styled("div")`
  margin-top: auto;
  display: flex;
  gap: 8px;
`;

const ActionButton = styled(Button)`
  && {
    flex: 1;
    justify-content: center;
    background: var(--alpha4);
  }
  &&:hover:not(:disabled) {
    background: var(--alpha8);
  }
`;

const Preview = styled("div")`
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--alpha4);
  border-radius: 12px;
  color: var(--text-secondary);
`;

const Row = styled("div")`
  display: flex;
  gap: 8px;
  padding: 4px 0;
`;

const Key = styled("div")`
  width: 80px;
  color: var(--text-secondary);
`;

const Value = styled("div")`
  flex: 1;
  min-width: 0;
  word-break: break-all;
`;

const humanSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

export function FileInfoColumn({
  fileName,
  fullPath,
  size,
  modifiedAt,
  columnIndex,
  onRegisterCount,
  onPreview,
  onDownload,
}: Props) {
  useEffect(() => {
    onRegisterCount(columnIndex, 0);
  }, [columnIndex, onRegisterCount]);

  return (
    <Container>
      <Preview>
        <Icon name="file-braces-corner" size={24} />
      </Preview>
      <Text variant="text-regular-md" ellipsis>
        {fileName}
      </Text>
      <div>
        <Row>
          <Key>
            <Text variant="text-small">Path</Text>
          </Key>
          <Value>
            <Text variant="text-small">
              <code>{fullPath}</code>
            </Text>
          </Value>
        </Row>
        <Row>
          <Key>
            <Text variant="text-small">Size</Text>
          </Key>
          <Value>
            <Text variant="text-small">{humanSize(size)}</Text>
          </Value>
        </Row>
        <Row>
          <Key>
            <Text variant="text-small">Modified</Text>
          </Key>
          <Value>
            <Text variant="text-small">
              {new Date(modifiedAt).toLocaleString()}
            </Text>
          </Value>
        </Row>
      </div>
      <Footer>
        <ActionButton
          variant="secondary"
          size="l"
          icon="circle-arrow-down"
          onClick={onDownload}
        >
          Download
        </ActionButton>
        <ActionButton
          variant="secondary"
          size="l"
          icon="eye"
          onClick={onPreview}
        >
          Preview
        </ActionButton>
      </Footer>
    </Container>
  );
}
